import { Link, useLocation } from 'react-router-dom'
import type { CalculatorMeta } from '@/types/calculator'
import { CATEGORIES, getCalcUrl } from '@/registry'
import { resolveLocaleFromPath, toCurrentLocalePath } from '@/i18n/routing'
import {
  getLocalizedCalculatorMeta,
  getLocalizedCategoryLabel,
  getLocalizedGroupCta,
} from '@/i18n/calculator-localization'

const categoryColors: Record<string, string> = {
  electrical: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  energy:     'bg-green-100 text-green-800 border-green-200',
  mechanical: 'bg-blue-100 text-blue-800 border-blue-200',
  thermal:    'bg-red-100 text-red-800 border-red-200',
  physics:    'bg-purple-100 text-purple-800 border-purple-200',
  converters: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  tools:      'bg-orange-100 text-orange-800 border-orange-200',
  generators: 'bg-amber-100 text-amber-800 border-amber-200',
}

const ctaByGroup: Record<string, string> = {
  calculators: 'Open Calculator →',
  converters:  'Open Converter →',
  tools:       'Open Tool →',
  generators:  'Open Generator →',
}

export function CalculatorCard({ calc }: { calc: CalculatorMeta }) {
  const { pathname } = useLocation()
  const { locale } = resolveLocaleFromPath(pathname)
  const cat = CATEGORIES.find(c => c.id === calc.category)
  const localizedCalc = getLocalizedCalculatorMeta(locale, calc)
  const group = (cat as { group?: string })?.group ?? ''
  const cta = getLocalizedGroupCta(locale, group) ?? ctaByGroup[group] ?? 'Open ->'
  const localizedCategoryLabel = getLocalizedCategoryLabel(locale, calc.category, cat?.label ?? '')
  const targetPath = toCurrentLocalePath(pathname, getCalcUrl(calc.slug, calc.category))
  return (
    <Link
      to={targetPath}
      className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-400 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-semibold text-gray-900 group-hover:text-brand-700 text-sm leading-snug">
          {localizedCalc.name}
        </h3>
        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded border ${categoryColors[calc.category]}`}>
          {cat?.icon} {localizedCategoryLabel}
        </span>
      </div>
      <p className="text-gray-500 text-xs leading-relaxed mb-3">{localizedCalc.description}</p>
      <span className="text-xs font-medium text-brand-600 group-hover:text-brand-700">
        {cta}
      </span>
    </Link>
  )
}
